import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select } from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";
import QueueSelectSingle from "../../components/QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const azureVoices = [
  "pt-BR-FabioNeural", "pt-BR-FranciscaNeural", "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural", "pt-BR-DonatoNeural", "pt-BR-ElzaNeural",
  "pt-BR-GiovannaNeural", "pt-BR-HumbertoNeural", "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural", "pt-BR-LeticiaNeural", "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural", "pt-BR-ValerioNeural", "pt-BR-YaraNeural"
];

const openAiTtsVoices = [
  "alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer",
  "ballad", "verse", "marin", "cedar"
];

const openAiTtsModels = [
  { value: "tts-1", label: "OpenAI TTS (rápido)" },
  { value: "tts-1-hd", label: "OpenAI TTS HD (qualidade)" },
  { value: "gpt-4o-mini-tts", label: "GPT-4o mini TTS" }
];

const inferTtsProvider = (voice, ttsProvider) => {
  if (ttsProvider === "azure" || ttsProvider === "openai") return ttsProvider;
  if (azureVoices.includes(voice)) return "azure";
  if (openAiTtsVoices.includes(voice)) return "openai";
  return "azure";
};

const getVoiceLabel = (voice) => {
  if (voice === "texto") return "Texto";
  if (azureVoices.includes(voice)) return voice.replace("pt-BR-", "").replace("Neural", "");
  return voice.charAt(0).toUpperCase() + voice.slice(1);
};

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
}));

const PromptSchema = Yup.object().shape({
    name: Yup.string().min(5, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
    prompt: Yup.string().min(50, "Muito curto!").required("Descreva o treinamento para Inteligência Artificial"),
    voice: Yup.string().required("Informe o modo para Voz"),
    max_tokens: Yup.number().required("Informe o número máximo de tokens"),
    temperature: Yup.number().required("Informe a temperatura"),
    apikey: Yup.string().required("Informe a API Key"),
    queueId: Yup.number().required("Informe a fila"),
    max_messages: Yup.number().required("Informe o número máximo de mensagens")
});

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const [showApiKey, setShowApiKey] = useState(false);

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        ttsProvider: "azure",
        ttsModel: "tts-1",
        openaiTtsInstructions: "",
        ttsSpeed: 1,
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
        maxMessages: 10,
        delay: 7
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt(prevState => ({
                    ...prevState,
                    ...data,
                    delay: data.delay ?? 7,
                    ttsProvider: data.ttsProvider || inferTtsProvider(data.voice, data.ttsProvider),
                    ttsModel: data.ttsModel || "tts-1",
                    openaiTtsInstructions: data.openaiTtsInstructions || "",
                    ttsSpeed: Number(data.ttsSpeed) || 1
                }));
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    const handleClose = () => {
        setPrompt(initialState);
        onClose();
    };

    const handleSavePrompt = async values => {
        const promptData = { ...values };
        if (!values.queueId) {
            toastError("Informe o setor");
            return;
        }
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    {promptId
                        ? `${i18n.t("promptModal.title.edit")}`
                        : `${i18n.t("promptModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.name")}
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                />
                                <FormControl fullWidth margin="dense" variant="outlined">
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.apikey")}
                                        name="apiKey"
                                        type={showApiKey ? 'text' : 'password'}
                                        error={touched.apiKey && Boolean(errors.apiKey)}
                                        helperText={touched.apiKey && errors.apiKey}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleToggleApiKey}>
                                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.prompt")}
                                    name="prompt"
                                    error={touched.prompt && Boolean(errors.prompt)}
                                    helperText={touched.prompt && errors.prompt}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                    rows={10}
                                    multiline={true}
                                />
                                <QueueSelectSingle />
                                <FormControl fullWidth margin="dense" variant="outlined">
                                    <InputLabel>{i18n.t("promptModal.form.voice")}</InputLabel>
                                    <Select
                                        value={values.voice === "texto" ? "texto" : "audio"}
                                        onChange={(e) => {
                                            if (e.target.value === "texto") {
                                                setFieldValue("voice", "texto");
                                                return;
                                            }
                                            const provider = inferTtsProvider(values.voice, values.ttsProvider);
                                            setFieldValue("ttsProvider", provider);
                                            setFieldValue("voice", provider === "openai" ? "nova" : "pt-BR-FabioNeural");
                                        }}
                                        label={i18n.t("promptModal.form.voice")}
                                    >
                                        <MenuItem value="texto">Apenas Texto</MenuItem>
                                        <MenuItem value="audio">Responder em áudio</MenuItem>
                                    </Select>
                                </FormControl>

                                {values.voice !== "texto" && (
                                    <>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Provedor de voz</InputLabel>
                                            <Field as={Select} name="ttsProvider" label="Provedor de voz"
                                                onChange={(e) => {
                                                    const provider = e.target.value;
                                                    setFieldValue("ttsProvider", provider);
                                                    setFieldValue("voice", provider === "openai" ? "nova" : "pt-BR-FabioNeural");
                                                }}
                                            >
                                                <MenuItem value="azure">Microsoft Azure</MenuItem>
                                                <MenuItem value="openai">OpenAI TTS</MenuItem>
                                            </Field>
                                        </FormControl>

                                        {(values.ttsProvider || "azure") === "azure" ? (
                                            <div className={classes.multFieldLine}>
                                                <FormControl fullWidth margin="dense" variant="outlined">
                                                    <InputLabel>Voz Azure</InputLabel>
                                                    <Field as={Select} name="voice" label="Voz Azure">
                                                        {azureVoices.map((v) => (
                                                            <MenuItem key={v} value={v}>{getVoiceLabel(v)}</MenuItem>
                                                        ))}
                                                    </Field>
                                                </FormControl>
                                                <Field
                                                    as={TextField}
                                                    label={i18n.t("promptModal.form.voiceKey")}
                                                    name="voiceKey"
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                />
                                                <Field
                                                    as={TextField}
                                                    label={i18n.t("promptModal.form.voiceRegion")}
                                                    name="voiceRegion"
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <FormControl fullWidth margin="dense" variant="outlined">
                                                    <InputLabel>Modelo OpenAI TTS</InputLabel>
                                                    <Field as={Select} name="ttsModel" label="Modelo OpenAI TTS">
                                                        {openAiTtsModels.map((m) => (
                                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                                        ))}
                                                    </Field>
                                                </FormControl>
                                                <FormControl fullWidth margin="dense" variant="outlined">
                                                    <InputLabel>Voz OpenAI</InputLabel>
                                                    <Field as={Select} name="voice" label="Voz OpenAI">
                                                        {openAiTtsVoices.map((v) => (
                                                            <MenuItem key={v} value={v}>{getVoiceLabel(v)}</MenuItem>
                                                        ))}
                                                    </Field>
                                                </FormControl>
                                                <Field
                                                    as={TextField}
                                                    label="Instruções de estilo (opcional)"
                                                    name="openaiTtsInstructions"
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                />
                                                <Field
                                                    as={TextField}
                                                    label="Velocidade"
                                                    name="ttsSpeed"
                                                    type="number"
                                                    inputProps={{ min: 0.25, max: 4, step: 0.05 }}
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                                
                                <div className={classes.multFieldLine}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.temperature")}
                                        name="temperature"
                                        error={touched.temperature && Boolean(errors.temperature)}
                                        helperText={touched.temperature && errors.temperature}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.max_tokens")}
                                        name="maxTokens"
                                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                                        helperText={touched.maxTokens && errors.maxTokens}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.max_messages")}
                                        name="maxMessages"
                                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                                        helperText={touched.maxMessages && errors.maxMessages}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                </div>
                                <Field
                                    as={TextField}
                                    type="number"
                                    inputProps={{ min: 0, max: 120 }}
                                    label={i18n.t("promptModal.form.delay") || "Delay da IA (segundos)"}
                                    name="delay"
                                    error={touched.delay && Boolean(errors.delay)}
                                    helperText={(touched.delay && errors.delay) || (i18n.t("promptModal.form.delayHelp") || "Aguarda este tempo antes de responder (agrupa sequência de mensagens do contato). Padrão: 7")}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default PromptModal;